/**
 * The member's own interest lists (docs/MarketplaceDesign.md section 3.4):
 * what they joined, where it stands, and leaving a list, which erases their
 * contact details from the vendor's lead (CR-34).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as marketApi from '../api/marketplace';
import { useToast } from './toast';
import LeadPill from './LeadPill';
import { Spinner, ErrorAlert, formatDate } from './ui';
import { cn, ui } from '../lib/ui';

export default function MyInterestLists() {
  const toast = useToast();
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLeads((await marketApi.listMyLeads()).leads);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function leave(lead) {
    try {
      await marketApi.actOnLead(lead.leadId, 'withdraw');
      toast.success(`You left ${lead.vendorName}'s list; they no longer see your contact details.`);
      load();
    } catch (err) {
      toast.error(err.message || 'Nothing changed');
    }
  }

  if (error) return <ErrorAlert error={error} />;
  if (!leads) return <Spinner label="Loading your interest lists..." />;
  if (!leads.length) return <p className={ui.muted}>You have not joined an interest list yet.</p>;
  return (
    <ul className="m-0 list-none p-0">
      {leads.map(l => (
        <li key={l.leadId} className={cn(ui.row, ui.spread, 'border-b border-z-line py-2')}>
          <span>
            <Link to={`/marketplace/vendors/${encodeURIComponent(l.vendorId)}`}>
              {l.vendorName}
            </Link>{' '}
            - {l.serviceName} <LeadPill status={l.status} />
            <span className={cn(ui.hint, 'block')}>Joined {formatDate(l.createdAt)}</span>
          </span>
          {(l.followUpSentAt || l.status === 'won' || l.status === 'lost') &&
            l.status !== 'voided' && (
              <Link
                className={cn(ui.btnSecondary, ui.btnSm)}
                to={`/marketplace/follow-up/${encodeURIComponent(l.leadId)}`}
              >
                {l.followUp ? 'Update your answers' : 'Tell us how it went'}
              </Link>
            )}
          {l.actions.includes('withdraw') && (
            <button type="button" className={cn(ui.btnGhost, ui.btnSm)} onClick={() => leave(l)}>
              Leave the list<span className="sr-only"> of {l.vendorName}</span>
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
